import { useEffect, useRef } from 'react';
import debounce from 'lodash/debounce';

// Constants
export const DRAFT_PREFIX = 'draft_';
export const MAX_DRAFTS = 50;
export const DRAFT_MAX_AGE = 7 * 24 * 60 * 60 * 1000; // 7 days
export const SAVE_DELAY = 500; // ms

// Types
interface DraftData {
  content: string;
  timestamp: number;
}

// Helper functions
export const enforceDraftLimit = (): void => {
  try {
    // Get all draft keys
    const draftKeys = Object.keys(localStorage)
      .filter(key => key.startsWith(DRAFT_PREFIX))
      .sort((a, b) => {
        // Sort by last modified timestamp
        const timeA = JSON.parse(localStorage.getItem(a) || '{"timestamp": 0}').timestamp || 0;
        const timeB = JSON.parse(localStorage.getItem(b) || '{"timestamp": 0}').timestamp || 0;
        return timeB - timeA;
      });

    // Remove oldest drafts if we exceed the limit
    if (draftKeys?.length > MAX_DRAFTS) {
      draftKeys
        .slice(MAX_DRAFTS)
        .forEach(key => localStorage.removeItem(key));
    }
  } catch (error) {
    console.error('Error enforcing draft limit:', error);
  }
};

export const cleanupOldDrafts = (): void => {
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(DRAFT_PREFIX)) {
        const draftData = localStorage.getItem(key);
        if (draftData) {
          const timestamp = JSON.parse(draftData).timestamp;
          if (Date.now() - timestamp > DRAFT_MAX_AGE) {
            localStorage.removeItem(key);
          }
        }
      }
    });
  } catch (error) {
    console.error('Error cleaning up drafts:', error);
  }
};

export const loadDraft = (chatId: string | null): string => {
  try {
    // Single source of truth for draft loading
    const drafts = {
      current: localStorage.getItem('draft_current'),
      new: localStorage.getItem('draft_new'),
      chatSpecific: chatId ? localStorage.getItem(`draft_${chatId}`) : null
    };

    const draftToUse = chatId === 'new' 
      ? (drafts.current || drafts.new)
      : (drafts.chatSpecific || drafts.new);

    if (draftToUse) {
      try {
        // Parse the JSON and extract just the content
        const parsedDraft = JSON.parse(draftToUse);
        return parsedDraft.content || '';
      } catch (e) {
        // Fallback for old draft format
        return draftToUse;
      }
    }
    return '';
  } catch (error) {
    console.error('Error loading draft:', error);
    return '';
  }
};

export const saveDraft = (input: string, chatId: string | null): void => {
  try {
    if (input.trim()) {
      const draftData: DraftData = {
        content: input,
        timestamp: Date.now()
      };

      const draftKey = !chatId || chatId === 'new' 
        ? 'draft_new' 
        : `draft_${chatId}`;

      localStorage.setItem(draftKey, JSON.stringify(draftData));
      
      if (chatId && chatId !== 'new') {
        localStorage.setItem('draft_current', JSON.stringify(draftData));
      }

      enforceDraftLimit();
    } else {
      const draftKey = !chatId || chatId === 'new'
        ? 'draft_new'
        : `draft_${chatId}`;

      localStorage.removeItem(draftKey);
      
      if (chatId && chatId !== 'new') {
        localStorage.removeItem('draft_current');
      }
    }
  } catch (error) {
    console.error('Error saving draft:', error);
  }
};

/**
 * Custom hook to manage chat drafts
 * 
 * @param chatId - The ID of the current chat
 * @param input - The current input value
 * @param setInput - Function to update the input value
 * @param inputRef - Reference to the input value
 * @param textareaRef - Reference to the textarea element
 * @param isSubmitting - Whether a message is currently being submitted
 */
export const useDraft = (
  chatId: string | null, 
  input: string, 
  setInput: (value: string) => void, 
  inputRef: React.MutableRefObject<string>, 
  textareaRef: React.MutableRefObject<HTMLTextAreaElement | null>, 
) => {
  // Create a debounced save function that persists between renders
  const debouncedSaveRef = useRef<ReturnType<typeof debounce> | null>(null);

  // Initialize the debounced save function once
  useEffect(() => {
    debouncedSaveRef.current = debounce((value: string) => {
      saveDraft(value, chatId);
    }, SAVE_DELAY);

    // Clean up old drafts once on mount
    cleanupOldDrafts();

    const flushPendingSaves = () => {
      if (debouncedSaveRef.current) {
        try {
          debouncedSaveRef.current.flush();
        } catch (err) {
          // In rare cases flush might throw if debounce hasn't been called yet
        }
      }
    };

    window.addEventListener('beforeunload', flushPendingSaves);

    return () => {
      flushPendingSaves();
      window.removeEventListener('beforeunload', flushPendingSaves);
      if (debouncedSaveRef.current) {
        debouncedSaveRef.current.cancel();
      }
    };
  }, [chatId]);

  // Load draft when chatId changes
  useEffect(() => {
    const draftContent = loadDraft(chatId);
    if (draftContent) {
      setInput(draftContent);
      if (inputRef) inputRef.current = draftContent;
      if (textareaRef.current) {
        textareaRef.current.value = draftContent;
        
        // Set cursor position to the end of the text
        setTimeout(() => {
          if (textareaRef.current) {
            textareaRef.current.focus();
            textareaRef.current.selectionStart = draftContent.length;
            textareaRef.current.selectionEnd = draftContent.length;
          }
        }, 0);
      }
    }
  }, [chatId, setInput, inputRef, textareaRef]);

  // Save draft when input changes
  useEffect(() => {
    if (debouncedSaveRef.current) {
      debouncedSaveRef.current(input);
    }
  }, [input, chatId]);

  // Function to clear drafts after successful submission
  const clearDrafts = () => {
    if (chatId) {
      localStorage.removeItem(`draft_${chatId}`);
      localStorage.removeItem('draft_current');
    }
  };

  return { clearDrafts };
};